import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LockKey, SignOut } from "@phosphor-icons/react";
import { toast } from "../ui/sonner";
import { checkAdminSession, adminLoginWithPassword, adminLogout } from "../../lib/api";

export const AdminGate = ({ children }) => {
  const [status, setStatus] = useState("checking");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();

  useEffect(() => {
    checkAdminSession()
      .then((ok) => setStatus(ok ? "authed" : "locked"))
      .catch(() => setStatus("locked"));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adminLoginWithPassword(email, password);
      setPassword("");
      setStatus("authed");
    } catch {
      toast.error("Invalid admin email or password");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
    } finally {
      setStatus("locked");
    }
  };

  if (status === "checking") {
    return (
      <div data-testid="admin-gate-checking" className="p-10 text-center font-mono text-xs text-zinc-500">
        Checking admin session&hellip;
      </div>
    );
  }

  if (status === "locked") {
    return (
      <div data-testid="admin-gate-locked" className="mx-auto flex max-w-sm flex-col gap-4 px-4 py-24 text-center">
        <LockKey size={28} className="mx-auto text-eb-accent" />
        <h2 className="font-heading text-xl uppercase text-white">Admin Login</h2>
        <p className="text-xs text-zinc-500">Sign in with the admin email and password to manage data, features and subscribers.</p>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <input
            type="email"
            data-testid="admin-gate-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email"
            className="rounded-md border border-white/10 bg-eb-surface px-3 py-2 text-sm text-white"
          />
          <input
            type="password"
            data-testid="admin-gate-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className="rounded-md border border-white/10 bg-eb-surface px-3 py-2 text-sm text-white"
          />
          <button
            type="submit"
            data-testid="admin-gate-submit-button"
            disabled={submitting || !email || !password}
            className="rounded-md border border-eb-accent/40 bg-eb-accent/10 px-3 py-2 font-mono text-xs uppercase text-white transition-colors hover:border-eb-accent disabled:opacity-50"
          >
            {submitting ? "Signing in\u2026" : "Log In"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div data-testid="admin-subnav" className="flex items-center justify-between border-b border-white/10 bg-eb-surface/50 px-4 py-2">
        <div className="flex gap-4">
          <Link
            to="/admin"
            data-testid="admin-subnav-data-link"
            className={`font-mono text-xs uppercase ${location.pathname === "/admin" ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}
          >
            Data
          </Link>
          <Link
            to="/feature-coverage"
            data-testid="admin-subnav-features-link"
            className={`font-mono text-xs uppercase ${location.pathname === "/feature-coverage" ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}
          >
            Features
          </Link>
          <Link
            to="/admin/subscribers"
            data-testid="admin-subnav-subscribers-link"
            className={`font-mono text-xs uppercase ${location.pathname === "/admin/subscribers" ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}
          >
            Subscribers
          </Link>
        </div>
        <button
          data-testid="admin-subnav-logout-button"
          onClick={handleLogout}
          className="flex items-center gap-1 font-mono text-xs uppercase text-zinc-500 hover:text-zinc-300"
        >
          <SignOut size={14} weight="bold" /> Log Out
        </button>
      </div>
      {children}
    </div>
  );
};
